import { match, P } from 'ts-pattern';
import {
  BANG_TYPE_FIELD_NAME,
  type PackageSummary,
  type ParsedObject,
  type ParsedObjectProperty,
  type ParsedRef,
  type ParsedSchema,
  type ParsedSchemaWithRef,
} from '../types/parsed';
import { EntityPart } from '../types/shared';

export const JSON_SCHEMA_REFERENCE_PREFIX = '#/schemas/';

export function cleanRefName(ref: ParsedRef) {
  return ref.$ref.replace(JSON_SCHEMA_REFERENCE_PREFIX, '');
}

export function findSchemaForEntityName(
  entityName: string,
  schemas: Map<string, ParsedSchema>,
  entityPart: EntityPart = EntityPart.State,
): ParsedObject | undefined {
  return Array.from(schemas.values()).find((schema) => {
    if ('object' in schema && schema.object.entity?.stateEntityFullName === entityName && schema.object.entity.part === entityPart) {
      return true;
    }

    return false;
  }) as ParsedObject | undefined;
}

export function getSchemaName(schema: ParsedSchemaWithRef | undefined, schemas: Map<string, ParsedSchema>): string {
  return match(schema)
    .with({ object: P.not(P.nullish) }, (s) => s.object.name)
    .with({ enum: P.not(P.nullish) }, (s) => s.enum.name)
    .with({ oneOf: P.not(P.nullish) }, (s) => s.oneOf.name)
    .with({ $ref: P.not(P.nullish) }, (s) => getSchemaName(schemas.get(cleanRefName(s)), schemas))
    .with({ array: P.not(P.nullish) }, (s) => getSchemaName(s.array.itemSchema, schemas))
    .with({ polymorph: P.not(P.nullish) }, (s) => s.polymorph.name)
    .otherwise(() => '');
}

export function getFullGRPCName(schema: ParsedSchemaWithRef | undefined): string {
  return match(schema)
    .with({ object: P.not(P.nullish) }, (s) => s.object.fullGrpcName)
    .with({ enum: P.not(P.nullish) }, (s) => s.enum.fullGrpcName)
    .with({ oneOf: P.not(P.nullish) }, (s) => s.oneOf.fullGrpcName)
    .with({ polymorph: P.not(P.nullish) }, (s) => s.polymorph.fullGrpcName)
    .with({ array: P.not(P.nullish) }, (s) => getFullGRPCName(s.array.itemSchema))
    .with({ $ref: P.not(P.nullish) }, (s) => cleanRefName(s))
    .otherwise(() => '');
}

// As per https://github.com/interxfi/j5 in the section about data type encoding
export function getScalarTypeForSchema(schema: ParsedSchema) {
  return match(schema)
    .with({ string: P.not(P.nullish) }, () => 'string' as const)
    .with({ bool: P.not(P.nullish) }, () => 'boolean' as const)
    .with({ float: P.not(P.nullish) }, () => 'number' as const)
    .with({ decimal: P.not(P.nullish) }, () => 'string' as const)
    .with({ bytes: P.not(P.nullish) }, () => 'string' as const)
    .with({ date: P.not(P.nullish) }, () => 'string' as const)
    .with({ timestamp: P.not(P.nullish) }, () => 'string' as const)
    .with({ key: P.not(P.nullish) }, () => 'string' as const)
    .with({ integer: { format: P.string.endsWith('64') } }, () => 'string' as const)
    .with({ integer: P.not(P.nullish) }, () => 'number' as const)
    .otherwise(() => undefined);
}

export function getPackageSummary(schema: ParsedSchemaWithRef | undefined): PackageSummary | undefined {
  return match(schema)
    .with({ object: P.not(P.nullish) }, (s) => s.object.package)
    .with({ enum: P.not(P.nullish) }, (s) => s.enum.package)
    .with({ oneOf: P.not(P.nullish) }, (s) => s.oneOf.package)
    .with({ polymorph: P.not(P.nullish) }, (s) => s.polymorph.package)
    .with({ array: P.not(P.nullish) }, (s) => getPackageSummary(s.array.itemSchema))
    .otherwise(() => undefined);
}

export function getObjectProperties(
  schema: ParsedSchemaWithRef | undefined,
  schemas: Map<string, ParsedSchema> = new Map(),
): Map<string, ParsedObjectProperty> | undefined {
  return match(schema)
    .with({ object: P.not(P.nullish) }, (s) => s.object.properties)
    .with({ oneOf: P.not(P.nullish) }, (s) => s.oneOf.properties)
    .with({ $ref: P.not(P.nullish) }, (s) => getObjectProperties(schemas.get(cleanRefName(s)), schemas))
    .with({ array: P.not(P.nullish) }, (s) => getObjectProperties(s.array.itemSchema, schemas))
    .with({ map: P.not(P.nullish) }, (s) => {
      const itemMap = getObjectProperties(s.map.itemSchema, schemas);
      const keyMap = getObjectProperties(s.map.keySchema, schemas);

      if (itemMap || keyMap) {
        return new Map([...(itemMap || new Map()), ...(keyMap || new Map())]);
      }

      return undefined;
    })
    .otherwise(() => undefined);
}

export function getPropertyByPath(path: string, schema: ParsedSchemaWithRef, schemas: Map<string, ParsedSchema>): ParsedSchema | undefined {
  const parts = path.split('.');
  let currentPart = parts.shift();
  let currentSchema = schema;

  while (currentPart) {
    const currentPartSchema = schemas.get(getFullGRPCName(currentSchema));
    const properties = getObjectProperties(currentPartSchema, schemas);

    if (!properties) {
      // Polymorphs don't have flat object properties, but their `!type` discriminator is addressable (e.g., for filterable fields)
      if (currentPart === BANG_TYPE_FIELD_NAME && currentPartSchema && 'polymorph' in currentPartSchema) {
        currentPart = parts.shift();
        continue;
      }

      return undefined;
    }

    const currentPartProperty = properties.get(currentPart);
    if (!currentPartProperty) {
      if (currentPart === BANG_TYPE_FIELD_NAME && currentPartSchema && 'oneOf' in currentPartSchema) {
        currentPart = parts.shift();
        continue;
      }

      return undefined;
    }

    currentSchema = currentPartProperty.schema;
    currentPart = parts.shift();
  }

  return match(currentSchema)
    .with({ $ref: P.not(P.nullish) }, (s) => schemas.get(cleanRefName(s)))
    .otherwise(() => currentSchema as ParsedSchema);
}

export function isSchemaArray(schema: ParsedSchemaWithRef) {
  return Boolean(schema && 'array' in schema && schema.array);
}
